/**
 * useLLMModels - Custom hook for fetching and managing LLM models.
 * Handles model loading when the LLM provider changes.
 *
 * Consolidated from 6 duplicate implementations across the codebase.
 * Single source of truth for model fetching, default selection, and serverless-aware filtering.
 */

import { useState, useEffect, useCallback } from 'react';
import { ModelService, URLService } from '../services';
import type { LLMModelOption, LLMProvider } from '../types';

export interface UseLLMModelsOptions {
  /** Usage context: 'video' for script generation (curated list), 'chat' for all models */
  context?: 'video' | 'chat';
  /** Whether to fetch models. Set to false for conditional tabs (e.g., AI Boost toggle off) */
  enabled?: boolean;
  /**
   * Active video source (e.g. 'ai_deforum'). When provided, the API returns only
   * models capable of handling that source. The hook auto-selects the API default
   * when the current model is no longer in the filtered list.
   */
  videoSource?: string;
}

export interface UseLLMModelsResult {
  availableModels: LLMModelOption[];
  isLoadingModels: boolean;
  /** True when the API fetch failed and the selector is showing built-in fallback models. */
  modelsError: boolean;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
}

export function useLLMModels(
  llmProvider: string,
  onModelChange?: (model: string) => void,
  options?: UseLLMModelsOptions
): UseLLMModelsResult {
  const { context = 'video', enabled = true, videoSource } = options ?? {};

  const [availableModels, setAvailableModels] = useState<LLMModelOption[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState(false);
  const [selectedModel, setSelectedModelState] = useState('');

  const setSelectedModel = useCallback(
    (model: string) => {
      setSelectedModelState(model);
      onModelChange?.(model);
    },
    [onModelChange]
  );

  // Fetch available models when LLM provider or video source changes
  useEffect(() => {
    if (!enabled) return;

    const fetchModelsForProvider = async () => {
      if (llmProvider === 'none') {
        setAvailableModels([]);
        setSelectedModelState('');
        return;
      }

      setIsLoadingModels(true);
      setModelsError(false);
      try {
        const apiUrl = URLService.getAPIBaseUrl();

        // Fetch both models and default model from API.
        // Pass videoSource so the API can filter to only capable models
        // (e.g. ai_deforum requires high-capability models).
        const [models, apiDefaultModel] = await Promise.all([
          ModelService.fetchModels(llmProvider as LLMProvider, apiUrl, context, videoSource),
          ModelService.fetchDefaultModel(llmProvider as LLMProvider, apiUrl, videoSource),
        ]);

        setAvailableModels(models);

        // Reconcile: if the currently-selected model is NOT in the new (possibly
        // filtered) list, force-select the API default so the user never submits
        // an incapable model (e.g. a free model after switching to ai_deforum).
        const currentModel = selectedModel;
        const currentStillAvailable =
          currentModel !== '' && models.some((m) => m.value === currentModel);

        if (!currentStillAvailable) {
          const defaultModel = ModelService.getDefaultModel(
            llmProvider as LLMProvider,
            models,
            apiDefaultModel || undefined
          );

          if (defaultModel) {
            console.log(
              `[useLLMModels] Current model "${currentModel}" not in filtered list; selecting default: ${defaultModel}`
            );
            setSelectedModelState(defaultModel);
            onModelChange?.(defaultModel);
          }
        }
      } catch (error) {
        console.error('Error fetching models:', error);
        const fallbackModels = ModelService.getFallbackModels(llmProvider as LLMProvider);
        setAvailableModels(fallbackModels);
        // Signal to consumers that these are built-in fallbacks, not a live list
        setModelsError(true);

        // Try to fetch default model even in fallback scenario
        const apiUrl = URLService.getAPIBaseUrl();
        const apiDefaultModel = await ModelService.fetchDefaultModel(
          llmProvider as LLMProvider,
          apiUrl,
          videoSource
        ).catch(() => null);

        const defaultModel = ModelService.getDefaultModel(
          llmProvider as LLMProvider,
          fallbackModels,
          apiDefaultModel || undefined
        );

        if (defaultModel) {
          setSelectedModelState(defaultModel);
          onModelChange?.(defaultModel);
        }
      } finally {
        setIsLoadingModels(false);
      }
    };

    fetchModelsForProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmProvider, context, enabled, videoSource]); // selectedModel intentionally excluded (read-only snapshot to avoid infinite loop)

  return { availableModels, isLoadingModels, modelsError, selectedModel, setSelectedModel };
}
